// TimelapseShareSeen — 絵ログ共有ボタンの「New!」バッジを「初回だけ」出すための
// seen フラグ永続化 (infrastructure 層).
//
// 背景 (consult 2026-07-08 案B):
//   - 絵ログ共有を **オーナー限定** で露出する。新機能訴求として owner の入口ボタンに
//     「New!」バッジを出すが、常時 New! だと陳腐化するので **初回に絵ログモーダルを
//     開いたら消す** (以後は非表示)。
//   - 「初回のみ」の判定に localStorage の seen フラグを使う (= layoutOnboardingSeen と
//     同流儀)。Classic / Modern 両入口で同じ store を参照するので、どちらで開いても
//     両方のバッジが消える。
//
// 設計 (`layoutOnboardingSeen.ts` と同流儀):
//   - boolean を文字列 `'1'` で保存 (= 在れば seen 済)。不在 / storage 不可は「未 seen」
//     (= まだ出す) 扱い。例外を throw しない。
//   - StorageLike 抽象 + sentinel probe。test で storage mock 注入可能。
//
// Tests: timelapseShareSeen.test.ts (storage-injection style)。

/** localStorage キー: 絵ログ共有の New! バッジを消したか (= 初回モーダルを開いたか). */
export const TIMELAPSE_SHARE_SEEN_KEY = 'oekaki:timelapse-share-seen';

/** Minimal Storage shape this module uses (layoutOnboardingSeen.ts と同流儀). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface TimelapseShareSeenStore {
  /** 既に New! バッジを消したか (= 初回モーダルを開いたか). 不在 / storage 不可 → false. */
  hasSeen(): boolean;
  /** 初回モーダルを開いたことを記録 (= 以後バッジ非表示). storage 不可なら no-op. */
  markSeen(): void;
  /** key を削除. テスト / リセット用. storage 不可なら no-op. */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:timelapse-share-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateTimelapseShareSeenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= hasSeen always false, markSeen silently drops).
   */
  storage?: StorageLike | null;
}

/**
 * `TimelapseShareSeenStore` のインスタンスを構築. storage 不可でも例外を出さず、
 * 「未 seen」扱い (= 出すが永続化されないだけ) で動作を継続する.
 */
export const createTimelapseShareSeenStore = (
  opts: CreateTimelapseShareSeenStoreOptions = {},
): TimelapseShareSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(TIMELAPSE_SHARE_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(TIMELAPSE_SHARE_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(TIMELAPSE_SHARE_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
